import { useState } from 'react';
import { TasksSidebar } from './TasksSidebar';
import { ContainerRecommendationList } from './ContainerRecommendationList';
import { useSelector } from "react-redux";

interface ContainerRecommendationsDashboardProps {
  data: any;
}

const tasks = [
  'Optimize Inventory Across 5 Locations',
  'Two Warehouses Affected by Heat Waves',
  'Dairy Product Category Recommendation Tasks',
  'Store #530 Recommendation Tasks',
  'Northeast Region Recommendation Tasks',
  'Warehouse #007 Recommendation Tasks',
];

const heatWaveLocations = ["Miyazaki", "Osaka"];

// Manufacturing-specific tasks for Sam
const manufacturingTasks = [
  "Typhoon alert for East and Southeast Asia",
  "Supplier Guangzhou Precision Parts Co. is facing delays",
  "Weather conditions unstable with heavy rain predicted in Southeast Asia",
];

// Sam-specific filtering rules
const taskFilters: Record<string, (location: string) => boolean> = {
  // Task 1 → show all containers
  "Typhoon alert for East and Southeast Asia": () => true,

  // Task 2 → only Nanjing
  "Supplier Guangzhou Precision Parts Co. is facing delays": (location) =>
    location === "Nanjing",

  // Task 3 → only Kuala Lumpur + Manila
  "Weather conditions unstable with heavy rain predicted in Southeast Asia": (location) =>
    ["Kuala Lumpur", "Manila"].includes(location),
};

function ContainerRecommendationsDashboard({ data }: ContainerRecommendationsDashboardProps) {
  const userEmail: any = useSelector((state: any) => state.auth?.user?.email);
  const isSam = userEmail?.trim()?.toLowerCase() === "sam@kimaru.ai";

  const myTasks = isSam ? manufacturingTasks : [];

  const [selectedTask, setSelectedTask] = useState(
    isSam ? myTasks[0] : tasks[0]
  );

  const containersArray: any[] = Object.values(data.containers) as any[];

  // Apply filtering rules
  const filteredContainers = containersArray.filter((container: any) => {
    if (isSam && taskFilters[selectedTask]) {
      return taskFilters[selectedTask](container.location);
    }

    // Default heatwave task for non-Sam
    if (!isSam && selectedTask === tasks[1]) {
      return heatWaveLocations.includes(container.location);
    }

    return true;
  });

  const countTotalRecommendations = (data: any): number => {
    return Object.values(data.containers).reduce(
      (total: number, container: any) =>
        total +
        (Array.isArray(container.recommendations)
          ? container.recommendations.length
          : 0),
      0
    );
  };

  const getRecommendationsCount = (): number => {
    if (isSam && taskFilters[selectedTask]) {
      return filteredContainers.reduce(
        (total: number, container: any) =>
          total +
          (Array.isArray(container.recommendations)
            ? container.recommendations.length
            : 0),
        0
      );
    }

    if (!isSam && selectedTask === tasks[1]) {
      return filteredContainers.reduce(
        (total: number, container: any) =>
          total +
          (Array.isArray(container.recommendations)
            ? container.recommendations.length
            : 0),
        0
      );
    }

    return countTotalRecommendations(data);
  };

  const recommendationsCount = getRecommendationsCount();

  const getContainersForTask = (task: string) => {
    // Apply same filtering rules as used for the selectedTask list
    if (isSam && taskFilters[task]) {
      return containersArray.filter((container: any) =>
        taskFilters[task](container.location)
      );
    }

    if (!isSam && task === tasks[1]) {
      return containersArray.filter((container: any) =>
        heatWaveLocations.includes(container.location)
      );
    }

    return containersArray;
  };

  const getTaskContainerCount = (task: string): number => {
    return getContainersForTask(task).length;
  };

  const taskContainerCounts: { [key: string]: number } = {};
  const tasksToShow = isSam ? myTasks : tasks;
  tasksToShow.forEach((task) => {
    taskContainerCounts[task] = getTaskContainerCount(task);
  });

  return (
    <div className="flex bg-gray-50 h-full overflow-hidden border-gray-200 border rounded-lg">
      {/* Sidebar */}
      <div className="flex-shrink-0 w-[23rem] h-full overflow-y-auto border-r border-gray-200 bg-white">
        <TasksSidebar
          tasks={isSam ? myTasks : tasks}
          selectedTask={selectedTask}
          onTaskSelect={setSelectedTask}
          taskContainerCounts={taskContainerCounts}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto h-full bg-white min-w-0">
        <ContainerRecommendationList
          numberOfRecommendations={recommendationsCount}
          data={data}
          containers={filteredContainers}
          selectedTask={selectedTask}
        />
      </div>
    </div>
  );
}

export default ContainerRecommendationsDashboard;
